import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react'
import { useCertificateContext } from './CertificateProvider'

interface PinContextType {
  pinCode: string
  setPinCode: (pin: string) => void
  savePinCode: (serialNumber: string, pin: string) => void
  showPinCode: boolean
  setShowPinCode: (show: boolean) => void
  getAllPinCodes: () => { [key: string]: string }
}

const PinContext = createContext<PinContextType | undefined>(undefined)

export const PinProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { selectedCertificate } = useCertificateContext()
  const [pinCodes, setPinCodes] = useState<{ [key: string]: string }>({})
  const [showPinCode, setShowPinCode] = useState(false)

  // Load PIN codes from localStorage on mount
  useEffect(() => {
    const storedPinCodes = localStorage.getItem('pinCodes')
    if (storedPinCodes) {
      setPinCodes(JSON.parse(storedPinCodes))
    }
  }, [])

  // Computed values
  const pinCode =
    pinCodes[selectedCertificate?.cert.serialNumber || ''] ||
    (selectedCertificate?.pinFromClient ? 'bypass' : '')

  const setPinCode = (pin: string) => {
    if (selectedCertificate) {
      setPinCodes((prev) => ({
        ...prev,
        [selectedCertificate.cert.serialNumber]: pin,
      }))
    }
  }

  const savePinCode = (serialNumber: string, pin: string) => {
    const updatedPinCodes = {
      ...pinCodes,
      [serialNumber]: pin,
    }

    setPinCodes(updatedPinCodes)
    localStorage.setItem('pinCodes', JSON.stringify(updatedPinCodes))
  }

  const getAllPinCodes = () => pinCodes

  const contextValue: PinContextType = {
    pinCode,
    setPinCode,
    savePinCode,
    showPinCode,
    setShowPinCode,
    getAllPinCodes,
  }

  return (
    <PinContext.Provider value={contextValue}>{children}</PinContext.Provider>
  )
}

export const usePinContext = () => {
  const context = useContext(PinContext)
  if (context === undefined) {
    throw new Error('usePinContext must be used within a PinProvider')
  }
  return context
}
